// src/Services/Auth/AuthService.ts
import {
    AccountInfo,
    Configuration,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from "@azure/msal-browser";
import { configService } from "../Config/ConfigService";

// Shape we *store* and return for native (non-MSAL) sessions
export interface NativeSession {
    token: string;       // normalized token (usually access_token)
    expiresAt: number;   // Unix ms (absolute expiry)
    [key: string]: any;  // keep original fields too
}

export interface IAuthService {
    // Bootstrapping
    onReady(): Promise<void>;
    initialize(): Promise<void>;

    // Auth state
    isAuthenticated(): boolean;

    // MSAL redirect flows (used by PreloginPage)
    loginRedirect(request?: RedirectRequest): Promise<void>;
    logoutRedirect(): Promise<void>;
    logout(): Promise<void>;

    // Token access (for APIs)
    getAccessToken(scopes?: string[]): Promise<string | null>;

    // Native session (CIAM native auth / OTP flows)
    getNativeSession(): NativeSession | null;
    setNativeSession(session: any | null): void;

    // OBO flags (used by OnBehalfOf + PreCertMiddleware / AbpnClient)
    setOnBehalfOf(preCertUserId: string | null): void;
    getOnBehalfOf(): string | null;
    setOboToken(token: string | null): void;
    getOboToken(): string | null;
    clearOboState(): void;

    // Session management hooks
    refreshIfPossible(): Promise<boolean>;
    subscribe(handler: () => void): () => void;

    // Profile cache (used by ProfileProvider)
    currentUser: any | null;
}

export default class AuthService implements IAuthService {
    private msalApp: PublicClientApplication | null = null;
    private msalConfig: Configuration | null = null;
    private readyPromise: Promise<void> | null = null;
    private loginRequest: RedirectRequest | null = null;

    private readonly nativeSessionKey = "native.session";
    private readonly oboTokenStorageKey = "precert.obo.token";
    private readonly onBehalfOfStorageKey = "precert.obo.preCertUserId";

    public currentUser: any | null = null;

    private subscribers = new Set<() => void>();

    // ------------------- Bootstrapping -------------------

    public async initialize(): Promise<void> {
        return this.onReady();
    }

    public async onReady(): Promise<void> {
        if (this.readyPromise) return this.readyPromise;
        this.readyPromise = this.initMsal();
        return this.readyPromise;
    }

    private async initMsal(): Promise<void> {
        const cfg: any = await configService.getConfiguration();
        const pre = cfg.PreCertspaIdentity || {};

        const authCfg = {
            clientId: pre.ClientId,
            authority: pre.Authority, // e.g. https://abpnexternal.ciamlogin.com/<tenantId>/v2.0
            redirectUri:
                pre.RedirectUri ||
                `${window.location.origin}/precert/`,
            postLogoutRedirectUri:
                pre.PostLogoutRedirectUri ||
                `${window.location.origin}/precert/`,
            knownAuthorities: pre.KnownAuthorities,
        };

        this.msalConfig = {
            auth: authCfg,
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false,
            },
            system: {
                loggerOptions: {
                    loggerCallback: (_level, message, containsPii) => {
                        if (containsPii) return;
                        if (message && /msal/i.test(message)) {
                            console.log("[MSAL]", message);
                        }
                    },
                },
            },
        };

        console.log(
            "[AuthService] msalConfig.auth.redirectUri",
            this.msalConfig.auth?.redirectUri
        );

        this.msalApp = new PublicClientApplication(this.msalConfig);

        console.log("[AuthService] calling msalApp.initialize()...");
        await this.msalApp.initialize();

        console.log("[AuthService] calling handleRedirectPromise...");
        const redirectResult = await this.msalApp.handleRedirectPromise();

        if (redirectResult?.account) {
            this.msalApp.setActiveAccount(redirectResult.account);
        } else {
            const accounts = this.msalApp.getAllAccounts();
            if (accounts.length === 1) {
                this.msalApp.setActiveAccount(accounts[0]);
            }
        }

        console.log(
            "[AuthService] no redirect result; accounts",
            this.msalApp.getAllAccounts()
        );

        // -------- Build default login request (scopes) --------
        const scopes: string[] = [];

        // Preferred: full API scope, e.g.
        // "api://ea639fc8-8e4d-48cd-82f3-3962264a8043/precert-api"
        if (Array.isArray(pre.ApiScopes)) {
            for (const s of pre.ApiScopes) {
                if (typeof s === "string" && s.trim()) {
                    scopes.push(s.trim());
                }
            }
        } else if (typeof pre.ApiScope === "string" && pre.ApiScope.trim()) {
            scopes.push(pre.ApiScope.trim());
        }

        // Always add OIDC core scopes
        if (!scopes.includes("openid")) scopes.push("openid");
        if (!scopes.includes("profile")) scopes.push("profile");
        if (!scopes.includes("offline_access")) scopes.push("offline_access");

        console.log("[AuthService] default login scopes", scopes);

        this.loginRequest = { scopes };
    }

    // ------------------- Auth state helpers -------------------

    public isAuthenticated(): boolean {
        // 1) MSAL account
        if (this.msalApp && this.msalApp.getAllAccounts().length > 0) {
            return true;
        }

        // 2) Native session
        const native = this.getNativeSession();
        if (native && native.token && native.expiresAt > Date.now()) {
            return true;
        }

        // 3) Staff OBO session (set by OnBehalfOf)
        try {
            const oboUserId = sessionStorage.getItem(this.onBehalfOfStorageKey);
            if (oboUserId) {
                return true;
            }
        } catch {
            /* ignore */
        }

        return false;
    }

    // ------------------- MSAL redirect APIs -------------------

    public async loginRedirect(request?: RedirectRequest): Promise<void> {
        await this.onReady();
        if (!this.msalApp) {
            throw new Error("MSAL not initialized");
        }

        const baseScopes = this.loginRequest?.scopes ?? [];
        const mergedScopes =
            request?.scopes && request.scopes.length > 0
                ? request.scopes
                : baseScopes.length > 0
                    ? baseScopes
                    : ["openid", "profile", "offline_access"];

        const merged: RedirectRequest = {
            scopes: mergedScopes,
            ...(this.loginRequest || {}),
            ...(request || {}),
        };

        // Ensure scopes are still present after spreading
        if (!merged.scopes || merged.scopes.length === 0) {
            merged.scopes = mergedScopes;
        }

        console.log("[AuthService] loginRedirect scopes", merged.scopes);

        return this.msalApp.loginRedirect(merged);
    }

    public async logoutRedirect(): Promise<void> {
        await this.onReady();
        if (!this.msalApp) return;

        const account: AccountInfo | null = this.msalApp.getActiveAccount();
        await this.msalApp.logoutRedirect({
            account: account ?? undefined,
        });
    }

    public async logout(): Promise<void> {
        this.setNativeSession(null);
        this.clearOboState();

        // 🔥 CRITICAL: clear MSAL active account BEFORE redirect
        if (this.msalApp) {
            this.msalApp.setActiveAccount(null);
        }

        this.notifySubscribers();

        await this.logoutRedirect();
    }

    // ------------------- Token for APIs -------------------

    public async getAccessToken(scopes?: string[]): Promise<string | null> {
        await this.onReady();
        if (!this.msalApp) return null;

        const account = this.msalApp.getActiveAccount();
        if (!account) {
            console.warn("[AuthService] getAccessToken: no active account");
            return null;
        }

        const effectiveScopes =
            scopes && scopes.length
                ? scopes
                : this.loginRequest?.scopes || ["openid", "profile", "offline_access"];

        const tokenRequest: SilentRequest = {
            scopes: effectiveScopes,
            account,
        };

        console.log("[AuthService] acquireTokenSilent scopes", effectiveScopes);

        try {
            const result = await this.msalApp.acquireTokenSilent(tokenRequest);
            return result.accessToken || null;
        } catch (e) {
            console.warn(
                "[AuthService] acquireTokenSilent failed; returning null",
                e
            );
            // Let caller handle 401 → Prelogin
            return null;
        }
    }

    // ------------------- Native session (OTP / native auth) -------------------

    public getNativeSession(): NativeSession | null {
        try {
            const raw = sessionStorage.getItem(this.nativeSessionKey);
            if (!raw) return null;

            const parsed: any = JSON.parse(raw);
            const now = Date.now();

            const token = parsed.token || parsed.access_token;

            let expiresAt: number;
            if (typeof parsed.expiresAt === "number") {
                expiresAt = parsed.expiresAt;
            } else if (typeof parsed.expiresAt === "string") {
                const n = Number(parsed.expiresAt);
                expiresAt = isNaN(n) ? now : n;
            } else if (typeof parsed.expires_in === "number") {
                expiresAt = now + parsed.expires_in * 1000;
            } else if (typeof parsed.expiresIn === "number") {
                expiresAt = now + parsed.expiresIn * 1000;
            } else {
                expiresAt = now + 60 * 60 * 1000; // default 1 hour
            }

            const normalized: NativeSession = {
                token,
                expiresAt,
                ...parsed,
            };

            // keep storage normalized
            sessionStorage.setItem(
                this.nativeSessionKey,
                JSON.stringify(normalized)
            );

            return normalized;
        } catch {
            return null;
        }
    }

    public setNativeSession(session: any | null): void {
        try {
            if (!session) {
                sessionStorage.removeItem(this.nativeSessionKey);
                this.notifySubscribers();
                return;
            }

            const now = Date.now();
            const expiresInSec: number =
                typeof session.expires_in === "number"
                    ? session.expires_in
                    : typeof session.expiresIn === "number"
                        ? session.expiresIn
                        : 60 * 60; // default 1 hr

            const expiresAt = now + expiresInSec * 1000;
            const token = session.token || session.access_token;

            const normalized: NativeSession = {
                token,
                expiresAt,
                ...session,
            };

            sessionStorage.setItem(
                this.nativeSessionKey,
                JSON.stringify(normalized)
            );
            this.notifySubscribers();
        } catch (e) {
            console.error("[AuthService] Failed to persist native session", e);
        }
    }

    // ------------------- OBO helpers -------------------

    public setOnBehalfOf(preCertUserId: string | null): void {
        try {
            if (!preCertUserId) {
                sessionStorage.removeItem(this.onBehalfOfStorageKey);
            } else {
                sessionStorage.setItem(this.onBehalfOfStorageKey, preCertUserId);
            }
            this.notifySubscribers();
        } catch {
            /* ignore */
        }
    }

    public getOnBehalfOf(): string | null {
        try {
            return sessionStorage.getItem(this.onBehalfOfStorageKey);
        } catch {
            return null;
        }
    }

    public setOboToken(token: string | null): void {
        try {
            if (!token) {
                sessionStorage.removeItem(this.oboTokenStorageKey);
            } else {
                sessionStorage.setItem(this.oboTokenStorageKey, token);
            }
            this.notifySubscribers();
        } catch {
            /* ignore */
        }
    }

    public getOboToken(): string | null {
        try {
            return sessionStorage.getItem(this.oboTokenStorageKey);
        } catch {
            return null;
        }
    }

    public clearOboState(): void {
        this.setOnBehalfOf(null);
        this.setOboToken(null);
    }

    // ------------------- Session management -------------------

    public async refreshIfPossible(): Promise<boolean> {
        // For now, only native session refresh logic:
        const session = this.getNativeSession();
        if (!session) return false;

        if (session.expiresAt && session.expiresAt > Date.now()) {
            return true;
        }

        this.setNativeSession(null);
        return false;
    }

    public subscribe(handler: () => void): () => void {
        this.subscribers.add(handler);
        return () => {
            this.subscribers.delete(handler);
        };
    }

    private notifySubscribers() {
        this.subscribers.forEach((fn) => {
            try {
                fn();
            } catch (e) {
                console.error("[AuthService] subscriber error", e);
            }
        });
    }
}
